// Canonical pricing — the single source of truth for OnArk's public plans.
//
// Before this file existed, plan prices lived forked in two places: the pricing
// page's display `TIERS` and the homepage `SoftwareApplicationJsonLd` Offers.
// They drifted — the schema still advertised a legacy $39/$99/$249 *monthly*
// model while the live page had moved to annual $390/$990/$2,490. Anything that
// reads structured data (Google, and AI engines / buying agents that parse
// SoftwareApplication schema) was being handed the wrong price.
//
// Everything price-related now derives from PLANS here: the pricing page cards,
// the SoftwareApplication schema Offers, and the machine-readable /pricing.md +
// /llms.txt route handlers. A drift-lock test (see __tests__/pricing-canonical)
// pins the three consumers to this list so they can never silently diverge again.

export interface Plan {
  /** Stable id, lowercase. Not user-facing. */
  id: string;
  /** Display name, e.g. "Starter". */
  name: string;
  /** Annual price in whole USD. New plans are annual-only (monthly is grandfathered). */
  priceAnnual: number;
  /** Church-size band this plan targets, e.g. "Under 500 weekly". */
  size: string;
  /** Included guest check-ins per year; null means unlimited. */
  checkInsPerYear: number | null;
  /** One-line positioning shown on the card. */
  description: string;
  /** Bullet list shown on the card (display copy, order matters). */
  features: string[];
  /** The plan we recommend — visually highlighted. NOT a popularity claim. */
  featured?: boolean;
  /** Optional reassurance line under the card (e.g. the overage note). */
  footnote?: string;
}

const PRICE_CURRENCY = 'USD';

// Overage policy — must match the billing truth in @onark/shared/billing
// (ANNUAL_PLAN_CONTRACT): included check-ins are free, additional check-ins are
// metered per use in Stripe. There is no automatic tier upgrade; upgrading is a
// manual, prorated plan change.
const OVERAGE_NOTE =
  'Go over? Extra check-ins bill per use — or upgrade any time, prorated.';

// Trial length lives in @onark/shared/billing as TRIAL_DAYS — it has to agree
// with the Stripe charge date, so it can't be owned by the marketing copy.

export const PLANS: Plan[] = [
  {
    checkInsPerYear: 100,
    description:
      'For small churches and church plants. Every feature included from day one.',
    features: [
      'Welcome Aboard class + launch kit included',
      'Unlimited class creation',
      '100 guest check-ins / year',
      'Planning Center sync',
      'Email + live chat support',
    ],
    footnote: OVERAGE_NOTE,
    id: 'starter',
    name: 'Starter',
    priceAnnual: 390,
    size: 'Under 500 weekly',
  },
  {
    checkInsPerYear: 500,
    description:
      'For growing churches with consistent check-ins. Our recommended plan.',
    featured: true,
    features: [
      'Welcome Aboard class + launch kit included',
      'Unlimited class creation',
      '500 guest check-ins / year',
      'Planning Center sync',
      'Email + live chat support',
    ],
    footnote: OVERAGE_NOTE,
    id: 'growth',
    name: 'Growth',
    priceAnnual: 990,
    size: '500 – 1,500 weekly',
  },
  {
    checkInsPerYear: null,
    description: 'For large and multi-campus churches with high volume.',
    features: [
      'Welcome Aboard class + launch kit included',
      'Unlimited class creation',
      'Unlimited guest check-ins',
      'Planning Center sync',
      'Email + live chat support',
    ],
    id: 'scale',
    name: 'Scale',
    priceAnnual: 2490,
    size: '1,500+ or multi-campus',
  },
];

/**
 * The cheapest annual price on the ladder, e.g. 390. Funnel copy leads with it
 * as "starting at" (never a bare "$390/year").
 *
 * The signup form pre-selects Growth ($990), not this plan. Landing pages used
 * to say "plans from $390/year" with nothing else, so a visitor read one number
 * and got charged another on day 21. Anything that anchors on this price must
 * also say "the plan you picked starts" and keep the full ladder reachable.
 *
 * The `featured` plan is a recommendation, NOT a claim about what churches buy.
 * Verified against live Stripe 2026-08-19: of 48 churches on the current
 * ladder, 33 are on Starter (69%), 12 Growth (25%), 3 Scale (6%), and 10 of the
 * last 12 signups chose Starter. Never call it most-picked or most-landed-on.
 */
export const STARTING_PRICE_ANNUAL = Math.min(...PLANS.map(p => p.priceAnnual));

/** STARTING_PRICE_ANNUAL for copy, e.g. "$390/year". Always read as "starting at". */
export const STARTING_PRICE = `${formatPrice(STARTING_PRICE_ANNUAL)}/year`;

/** Human-formatted annual price, e.g. 2490 → "$2,490". */
export function formatPrice(priceAnnual: number): string {
  return `$${priceAnnual.toLocaleString('en-US')}`;
}

/**
 * schema.org Offer for a plan. Annual billing (P1Y) — this is what fixed the
 * stale monthly schema. Consumed by SoftwareApplicationJsonLd.
 */
export function planToSchemaOffer(plan: Plan): Record<string, unknown> {
  return {
    '@type': 'Offer',
    name: plan.name,
    price: String(plan.priceAnnual),
    priceCurrency: PRICE_CURRENCY,
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      billingDuration: 'P1Y',
      billingIncrement: 1,
      price: plan.priceAnnual,
      priceCurrency: PRICE_CURRENCY,
      unitText: 'ANN',
    },
  };
}
